import { useMutation } from '@tanstack/react-query';
import { useState, useCallback, useRef, useEffect } from 'react';
import axios from '../utils/axios-config';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const MEDIA_BASE_URL = 'https://media.tracverse.com/';

// ═══════════════════════════════════════════════════════════════════════════
// STANDARDIZED UPLOAD API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Delete a single file from S3 (via standardized endpoint)
 */
export async function deleteS3File(urlOrKey) {
  const isUrl = urlOrKey?.startsWith('http://') || urlOrKey?.startsWith('https://');
  const response = await axios.delete(`${API_BASE_URL}/api/upload/file`, {
    data: isUrl ? { url: urlOrKey } : { fileKey: urlOrKey },
  });
  return response.data;
}

/**
 * Delete multiple files from S3 in a batch (more efficient)
 */
export async function deleteS3Files(urlsOrKeys) {
  if (!urlsOrKeys || urlsOrKeys.length === 0) {
    return { success: true, deleted: 0, failed: 0 };
  }

  const urls = [];
  const keys = [];

  for (const item of urlsOrKeys) {
    if (item?.startsWith('http://') || item?.startsWith('https://')) {
      urls.push(item);
    } else if (item) {
      keys.push(item);
    }
  }

  const response = await axios.delete(`${API_BASE_URL}/api/upload/files`, {
    data: { urls, keys },
  });
  return response.data;
}

/**
 * Delete old file if replaced with new one
 */
export async function deleteS3FileIfReplaced(oldUrl, newUrl) {
  if (!oldUrl || oldUrl === newUrl) {
    return { success: true, deleted: false };
  }
  const response = await axios.post(`${API_BASE_URL}/api/upload/cleanup-replaced`, {
    oldUrl,
    newUrl,
  });
  return response.data;
}

/**
 * Upload folder types - maps to backend storage paths
 */
export const UPLOAD_FOLDERS = {
  PROFILE_AVATAR: 'profile-image',
  PROFILE_BACKGROUND: 'profile-background',
  DISCUSSION_BANNER: 'discussion-banner',
  DISCUSSION_IMAGE: 'discussion-image',
  DISCUSSION_VIDEO: 'discussion-video',
  PARTICLE_COVER: 'particle-cover-image',
  PARTICLE_IMAGE: 'particle-image',
  PARTICLE_VIDEO: 'particle-video',
};

/**
 * Default file validation options
 */
const DEFAULT_VALIDATION = {
  maxSize: 5 * 1024 * 1024, // 5MB
  acceptedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
};

/**
 * Extract file key from URL for deletion
 */
export const getFileKeyFromUrl = (url) => {
  if (!url) return null;
  return url.replace(MEDIA_BASE_URL, '');
};

/**
 * Validate file before upload
 */
export const validateFile = (file, options = {}) => {
  const { maxSize, acceptedTypes } = { ...DEFAULT_VALIDATION, ...options };

  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (maxSize && file.size > maxSize) {
    const maxMB = Math.round(maxSize / (1024 * 1024));
    return { valid: false, error: `File size must be less than ${maxMB}MB` };
  }

  if (acceptedTypes && acceptedTypes.length > 0) {
    if (!acceptedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type not supported. Accepted: ${acceptedTypes.join(', ')}`,
      };
    }
  }

  return { valid: true, error: null };
};

/**
 * useFileUpload - Generic reusable upload hook
 *
 * @param {string} uploadType - One of UPLOAD_FOLDERS values
 * @param {object} options - Configuration options
 * @param {number} options.maxSize - Max file size in bytes (default 5MB)
 * @param {string[]} options.acceptedTypes - Accepted MIME types
 * @param {function} options.onSuccess - Callback on successful upload
 * @param {function} options.onError - Callback on error
 * @param {function} options.onProgress - Callback for progress updates
 *
 * @returns {object} Upload state and functions
 */
export function useFileUpload(uploadType, options = {}) {
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [uploadedKey, setUploadedKey] = useState(null);
  const [error, setError] = useState(null);

  const { maxSize, acceptedTypes, onSuccess, onError, onProgress } = {
    ...DEFAULT_VALIDATION,
    ...options,
  };

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post(
        `${API_BASE_URL}/omniverse/upload/${uploadType}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const pct = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setProgress(pct);
              onProgress?.(pct);
            }
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success && data.url) {
        setUploadedUrl(data.url);
        setUploadedKey(data.fileKey);
        setError(null);
        onSuccess?.(data);
      }
    },
    onError: (err) => {
      const message =
        err.response?.data?.error || err.message || 'Upload failed';
      setError(message);
      onError?.(message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (fileKey) => {
      const response = await axios.delete(
        `${API_BASE_URL}/omniverse/upload/delete-file`,
        { data: { fileKey } }
      );
      return response.data;
    },
  });

  /**
   * Upload a file
   */
  const upload = useCallback(
    async (file) => {
      // Reset state
      setError(null);
      setProgress(0);

      // Validate
      const validation = validateFile(file, { maxSize, acceptedTypes });
      if (!validation.valid) {
        setError(validation.error);
        onError?.(validation.error);
        return null;
      }

      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      // Upload
      const formData = new FormData();
      formData.append('file', file);

      try {
        const result = await uploadMutation.mutateAsync(formData);
        return result;
      } catch (err) {
        // Cleanup preview on error
        URL.revokeObjectURL(previewUrl);
        setPreview(null);
        return null;
      }
    },
    [maxSize, acceptedTypes, onError, uploadMutation]
  );

  /**
   * Delete the uploaded file
   */
  const deleteUpload = useCallback(async () => {
    if (uploadedKey) {
      try {
        await deleteMutation.mutateAsync(uploadedKey);
        setUploadedUrl(null);
        setUploadedKey(null);
        if (preview) {
          URL.revokeObjectURL(preview);
          setPreview(null);
        }
        return true;
      } catch (err) {
        return false;
      }
    }
    return false;
  }, [uploadedKey, preview, deleteMutation]);

  /**
   * Delete a file by URL (for cleaning up old uploads)
   */
  const deleteByUrl = useCallback(
    async (url) => {
      const key = getFileKeyFromUrl(url);
      if (key) {
        try {
          await deleteMutation.mutateAsync(key);
          return true;
        } catch (err) {
          return false;
        }
      }
      return false;
    },
    [deleteMutation]
  );

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setProgress(0);
    setPreview(null);
    setUploadedUrl(null);
    setUploadedKey(null);
    setError(null);
  }, [preview]);

  /**
   * Clear preview without deleting uploaded file
   */
  const clearPreview = useCallback(() => {
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
  }, [preview]);

  return {
    // State
    progress,
    preview,
    uploadedUrl,
    uploadedKey,
    error,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Actions
    upload,
    deleteUpload,
    deleteByUrl,
    reset,
    clearPreview,

    // Raw mutations (for advanced use)
    uploadMutation,
    deleteMutation,
  };
}

/**
 * useUploadQueue - Manage multiple pending uploads with cleanup
 *
 * Tracks files uploaded during a session and cleans up any
 * that weren't saved when the component unmounts or form is cancelled.
 *
 * Now uses batch delete for efficiency!
 */
export function useUploadQueue() {
  const [pendingUploads, setPendingUploads] = useState([]);
  const [savedUrls, setSavedUrls] = useState([]);

  // Use batch delete mutation for efficiency
  const deleteMutation = useMutation({
    mutationFn: async (urls) => {
      return deleteS3Files(urls);
    },
  });

  /**
   * Track a new upload
   */
  const trackUpload = useCallback((url, fileKey) => {
    setPendingUploads((prev) => [...prev, { url, fileKey }]);
  }, []);

  /**
   * Mark URLs as saved (won't be cleaned up)
   */
  const markAsSaved = useCallback((urls) => {
    const urlArray = Array.isArray(urls) ? urls : [urls];
    setSavedUrls((prev) => [...prev, ...urlArray.filter(Boolean)]);
  }, []);

  /**
   * Clean up unsaved uploads (uses batch delete)
   */
  const cleanup = useCallback(async () => {
    const urlsToDelete = pendingUploads
      .filter((upload) => !savedUrls.includes(upload.url))
      .map((upload) => upload.url)
      .filter(Boolean);

    if (urlsToDelete.length > 0) {
      try {
        await deleteMutation.mutateAsync(urlsToDelete);
      } catch (err) {
        console.error('Failed to cleanup uploads:', err);
      }
    }

    setPendingUploads([]);
    setSavedUrls([]);
  }, [pendingUploads, savedUrls, deleteMutation]);

  /**
   * Clear tracking without deleting
   */
  const clearTracking = useCallback(() => {
    setPendingUploads([]);
    setSavedUrls([]);
  }, []);

  return {
    pendingUploads,
    savedUrls,
    trackUpload,
    markAsSaved,
    cleanup,
    clearTracking,
    isCleaningUp: deleteMutation.isPending,
  };
}

/**
 * usePendingUploads - Auto-cleanup hook for pending uploads
 *
 * A simpler ref-based approach that automatically cleans up
 * uncommitted uploads on component unmount.
 *
 * Usage:
 *   const uploads = usePendingUploads();
 *
 *   // After upload
 *   uploads.track(uploadedUrl);
 *
 *   // Track original value for replacement cleanup
 *   uploads.trackOriginal(existingThumbnail);
 *
 *   // On save success
 *   uploads.commit([finalUrl1, finalUrl2]);
 *
 *   // On manual cancel (automatic on unmount)
 *   await uploads.cleanup();
 */
export function usePendingUploads() {
  const pendingRef = useRef(new Set());
  const committedRef = useRef(new Set());
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  const track = useCallback((url) => {
    if (url) pendingRef.current.add(url);
  }, []);

  const trackOriginal = useCallback((url) => {
    if (url) pendingRef.current.add(url);
  }, []);

  const commit = useCallback((urls) => {
    const urlArray = Array.isArray(urls) ? urls : [urls];
    urlArray.forEach((url) => {
      if (url) {
        committedRef.current.add(url);
        pendingRef.current.delete(url);
      }
    });
  }, []);

  const cleanup = useCallback(async () => {
    const toDelete = Array.from(pendingRef.current).filter(
      (url) => !committedRef.current.has(url)
    );

    if (toDelete.length === 0) {
      pendingRef.current.clear();
      committedRef.current.clear();
      return { deleted: 0, failed: 0 };
    }

    setIsCleaningUp(true);
    try {
      const result = await deleteS3Files(toDelete);
      pendingRef.current.clear();
      committedRef.current.clear();
      return { deleted: result.deleted || 0, failed: result.failed || 0 };
    } catch (err) {
      console.error('Cleanup failed:', err);
      return { deleted: 0, failed: toDelete.length };
    } finally {
      setIsCleaningUp(false);
    }
  }, []);

  const clear = useCallback(() => {
    pendingRef.current.clear();
    committedRef.current.clear();
  }, []);

  // Auto-cleanup on unmount
  useEffect(() => {
    return () => {
      const toDelete = Array.from(pendingRef.current).filter(
        (url) => !committedRef.current.has(url)
      );
      if (toDelete.length > 0) {
        // Fire and forget cleanup on unmount
        deleteS3Files(toDelete).catch(console.error);
      }
    };
  }, []);

  return {
    track,
    trackOriginal,
    commit,
    cleanup,
    clear,
    isCleaningUp,
    get pendingCount() {
      return pendingRef.current.size - committedRef.current.size;
    },
  };
}

// ============ Legacy Hooks (for backwards compatibility) ============

/**
 * @deprecated Use useFileUpload(UPLOAD_FOLDERS.PROFILE_AVATAR) instead
 */
export function useUploadProfileImage(progress, options = {}) {
  return useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post(
        '/omniverse/upload/profile-image',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true, // Ensure cookies are sent
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              progress((progressEvent.loaded / progressEvent.total) * 100);
            }
          },
        }
      );
      return response.data;
    },
    ...options,
  });
}

/**
 * @deprecated Use useFileUpload(UPLOAD_FOLDERS.PROFILE_BACKGROUND) instead
 */
export function useUploadProfileBackground(progress, options = {}) {
  return useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post(
        '/omniverse/upload/profile-background',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true, // Ensure cookies are sent
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              progress((progressEvent.loaded / progressEvent.total) * 100);
            }
          },
        }
      );
      return response.data;
    },
    ...options,
  });
}

/**
 * @deprecated Use useUploadQueue().cleanup() or useFileUpload().deleteByUrl() instead
 */
export function useDeleteFile(options = {}) {
  return useMutation({
    mutationFn: async (fileKey) => {
      const response = await axios.delete(
        '/omniverse/upload/delete-file',
        { data: { fileKey }, withCredentials: true }
      );
      return response.data;
    },
    ...options,
  });
}

/**
 * @deprecated Use useFileUpload(UPLOAD_FOLDERS.PARTICLE_COVER) instead
 */
export function useUploadCoverImage(progress, options = {}) {
  return useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post(
        '/omniverse/upload/particle-cover-image',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true, // Ensure cookies are sent
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              progress((progressEvent.loaded / progressEvent.total) * 100);
            }
          },
        }
      );
      return response.data;
    },
    ...options,
  });
}
